import React, { useCallback, useState } from 'react';
import { ButtonAction } from '@amzn/storm-ui-page-header';
import { ACTION_STATE, ActionDropdownGroup, POSITION } from '../types';
import {
  ActionDropdownTrigger,
  StyledDropdownAction as DropdownAction,
  StyledMenuItemGroup,
  StyledMenuItemLink,
} from './styles';

export const ActionsDropdown: React.VFC<{
  id: string;
  groups?: ActionDropdownGroup[];
  label: string;
  position?: POSITION;
  selectedValue?: string;
  /*
   * Optional and enum-typed to match DropdownType.state, which is what callers
   * spread in. An absent state means enabled: both comparisons below go false.
   */
  state?: ACTION_STATE;
  toolTip?: string;
  tooltipPosition?: POSITION;
  ariaLabel?: string;
}> = ({
  id,
  groups,
  label,
  position,
  toolTip,
  tooltipPosition,
  state,
  ariaLabel,
}) => {
  const [active, setActive] = useState(false);
  const isDisabled = state === ACTION_STATE.DISABLED;
  const isLoading = state === ACTION_STATE.LOADING;
  // A dropdown holding exactly one group with exactly one link renders as a
  // plain button instead.
  const singleGroup = groups?.length === 1 ? groups[0] : undefined;
  const singleLink =
    singleGroup?.links?.length === 1 ? singleGroup.links[0] : undefined;

  const onClick = useCallback(() => {
    if (!isDisabled) {
      setActive(!active);
    }
  }, [active, setActive]);

  const onClose = useCallback(() => {
    setActive(false);
  }, [setActive]);

  if (singleGroup && singleLink) {
    const id = singleGroup.id;
    const link = singleLink;
    const label = link.label ?? singleGroup.label;

    return (
      <ButtonAction
        id={id}
        label={label}
        data-eventid={id}
        data-ccx-e2e-id={`aac-button-${id}`}
        disabled={isDisabled}
        onClick={() => {
          window.location.assign(link.url);
        }}
        aria-label={label}
        {...(toolTip?.length && {
          tooltipContent: toolTip,
          tooltipPosition: tooltipPosition,
          'aria-haspopup': true,
        })}
      />
    );
  }

  return (
    <DropdownAction
      id={id}
      disabled={isDisabled}
      loading={isLoading}
      helptipText={toolTip}
      helptipTextPosition={tooltipPosition}
      isOpen={active}
      onClick={onClick}
      onClose={onClose}
      position={position}
      trigger={
        <ActionDropdownTrigger
          disabled={isDisabled}
          aria-label={ariaLabel}
          data-ccx-e2e-id={`aac-action-${id}`}
        >
          {label}
        </ActionDropdownTrigger>
      }
    >
      {groups?.map((group) => (
        <StyledMenuItemGroup key={group.id}>
          {group.links?.map((link) => (
            <StyledMenuItemLink
              key={link.id}
              id={link.id}
              href={link.url}
              onClick={link.onClickCallback}
              disabled={link.isDisabled}
              target="_self"
            >
              {link.label}
            </StyledMenuItemLink>
          ))}
        </StyledMenuItemGroup>
      ))}
    </DropdownAction>
  );
};
